import { useState, useEffect, useCallback, useMemo } from 'react';
import { RowSelectionMode, SelectionState, UseRowSelectionReturn } from '@/types';

/**
 * A fresh empty state every time, never a shared constant: `selectedIds` and
 * `anchorBaseIds` are mutable Sets, and one stray `.add()` on a shared instance
 * would leave every later "cleared" selection starting non-empty.
 */
const emptyState = (): SelectionState => ({
  selectedIds: new Set<number>(),
  lastClickedId: null,
  anchorBaseIds: new Set<number>(),
});

/**
 * Which of the four list gestures the modifiers spell out.
 *
 * Shift and Cmd/Ctrl together are their OWN gesture, not Shift winning a
 * precedence contest: Explorer and the macOS list views all read Cmd/Ctrl+Shift
 * as "extend the range and KEEP what I picked by hand", and so does our other
 * selection surface, the MTM grid (`useCellSelection`, which rewrites only the
 * last range on Shift). Collapsing it into plain `range` silently threw away the
 * rows a trader had Cmd-clicked before reaching for the contiguous block.
 */
export function selectionModeFromEvent(e: {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}): RowSelectionMode {
  if (e.shiftKey) return e.metaKey || e.ctrlKey ? 'range-add' : 'range';
  if (e.metaKey || e.ctrlKey) return 'toggle';
  return 'replace';
}

/** The three writers a row gesture can reach — the stable callbacks, not the hook object. */
export interface SelectionModeHandlers {
  selectOnly: (id: number) => void;
  toggleRow: (id: number) => void;
  selectRange: (id: number, visibleIds: number[], additive?: boolean) => void;
}

/**
 * Route one gesture to the writer it means. Lives here rather than inline in
 * TradesPage because this is where all four modes converge, and TradesPage is
 * not renderable in tests — inline, the mapping had no durable guard at all.
 */
export function applySelectionMode(
  handlers: SelectionModeHandlers,
  mode: RowSelectionMode,
  id: number,
  visibleIds: number[],
): void {
  switch (mode) {
    case 'toggle':
      return handlers.toggleRow(id);
    case 'range':
      return handlers.selectRange(id, visibleIds, false);
    case 'range-add':
      return handlers.selectRange(id, visibleIds, true);
    case 'replace':
      return handlers.selectOnly(id);
    default: {
      // Same compile-time guard as bo-posting-copy: a fifth mode must not land
      // silently on whichever branch happens to be last. Runtime stays soft —
      // an unknown gesture leaves the selection alone rather than guessing.
      const unknown: never = mode;
      void unknown;
      return;
    }
  }
}

/**
 * Row selection for the TradesPage blotter — the standard list model (LR-699).
 *
 * A plain click selects ONLY the clicked row, and clicking the row that is
 * already the sole selection clears it; Cmd/Ctrl+click toggles one row in or out
 * (the additive model LR-539 had put on the plain click); Shift+click takes the
 * display-order range from the anchor, and Cmd/Ctrl+Shift+click adds that range
 * to what is already selected. Space and Enter on the focused row route to the
 * same modes through the row's own key handler (LR-694), and Shift+Arrow extends
 * from the anchor as it moves the focus ring.
 *
 * `anchorBaseIds` is what a range op unions onto, and what makes it idempotent:
 * every repeat recomputes from the same floor, so a range both grows and SHRINKS
 * instead of accreting. A gesture that REPLACES the selection resets that floor
 * — the rows it dropped are gone by the trader's own hand, and carrying them
 * would let the next Cmd/Ctrl+Shift hand them back.
 *
 * The bulk bar's "Select all N" is the only batch affordance — the blotter has
 * no checkbox column and no preset picker. `pruneToVisible` keeps only the
 * still-visible ids after a filter or search change, narrowing the batch instead
 * of wiping it; Escape and a view switch clear.
 */
export function useRowSelection(activeViewId: string): UseRowSelectionReturn {
  const [state, setState] = useState<SelectionState>(emptyState);

  const clearSelection = useCallback(() => {
    // Bail out when there is nothing to clear, so the view-switch effect below
    // does not push a fresh object (and a re-render) on every mount. All three
    // fields are checked rather than the one that happens to imply the others.
    setState((prev) =>
      prev.selectedIds.size === 0 && prev.lastClickedId === null && prev.anchorBaseIds.size === 0
        ? prev
        : emptyState(),
    );
  }, []);

  // Clear on view switch (UI-SPEC Selection State Rules #1). A counterparty tab
  // now opens with nothing selected: the LR-539 seed that pre-selected all of
  // that CP's active trades is gone (LR-699).
  useEffect(() => {
    clearSelection();
  }, [activeViewId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape clears — skipped whenever a dialog is open so it doesn't fight a bulk
  // modal. Guard on BOTH the event target's ancestry AND any mounted dialog: if a
  // portal/toast has stolen focus, e.target is outside the trap and the ancestry
  // check alone would miss it, wiping the whole selection as the modal closes.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        const target = e.target as HTMLElement | null;
        if (target?.closest('[role="dialog"]')) return;
        if (document.querySelector('[role="dialog"]')) return;

        setState((prev) => (prev.selectedIds.size > 0 ? emptyState() : prev));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Plain click: keep only this row. Click again on the sole selected row to
  // clear; clicking one row out of several NARROWS to it rather than clearing.
  const selectOnly = useCallback((id: number) => {
    setState((prev) => {
      if (prev.selectedIds.size === 1 && prev.selectedIds.has(id)) return emptyState();
      return {
        selectedIds: new Set<number>([id]),
        lastClickedId: id,
        anchorBaseIds: new Set<number>([id]),
      };
    });
  }, []);

  // Cmd/Ctrl+click: add the id, or remove it if already selected.
  const toggleRow = useCallback((id: number) => {
    setState((prev) => {
      const next = new Set(prev.selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedIds: next, lastClickedId: id, anchorBaseIds: new Set(next) };
    });
  }, []);

  // Shift+click: the inclusive range between the anchor and this row in display
  // order. Plain Shift replaces the selection with it; `additive` (Cmd/Ctrl+Shift)
  // unions it onto the hand-picked rows the anchor was placed among. The anchor
  // and its base both stay put, so repeated shift clicks grow and shrink the
  // range from the same origin instead of piling up.
  const selectRange = useCallback((id: number, visibleIds: number[], additive = false) => {
    setState((prev) => {
      const anchor = prev.lastClickedId;
      if (anchor !== null) {
        const from = visibleIds.indexOf(anchor);
        const to = visibleIds.indexOf(id);
        if (from !== -1 && to !== -1) {
          const range = visibleIds.slice(Math.min(from, to), Math.max(from, to) + 1);
          const selectedIds = additive
            ? new Set<number>([...prev.anchorBaseIds, ...range])
            : new Set<number>(range);
          // A plain Shift REPLACES, so the rows picked by hand are gone the moment
          // the user makes that gesture — carrying the old base past it would let
          // the next Cmd/Ctrl+Shift put them back. Only the anchor row survives,
          // which keeps the range shrinkable (the anchor is always inside it).
          return {
            selectedIds,
            lastClickedId: anchor,
            anchorBaseIds: additive ? prev.anchorBaseIds : new Set<number>([anchor]),
          };
        }
      }
      // No anchor yet, or a filter has hidden it — start a new one here. Additive
      // still means additive: Cmd/Ctrl+Shift must never be the gesture that wipes.
      const selectedIds = additive
        ? new Set<number>([...prev.selectedIds, id])
        : new Set<number>([id]);
      return { selectedIds, lastClickedId: id, anchorBaseIds: new Set(selectedIds) };
    });
  }, []);

  // Select exactly the current filtered view (the bulk bar's "Select all N").
  // Replaces the selection with the visible ids so it can never leak a hidden row.
  // Anchors on the first row so a following Shift+click trims the batch from the
  // top instead of collapsing it to one row.
  const selectAllVisible = useCallback((visibleIds: number[]) => {
    const selectedIds = new Set<number>(visibleIds);
    setState({
      selectedIds,
      lastClickedId: visibleIds[0] ?? null,
      anchorBaseIds: new Set(selectedIds),
    });
  }, []);

  // Drop any selected id no longer visible (sidebar filter or search hid it),
  // keeping the rest — the FloatingActionBar/bulk-bar count then reflects only
  // what is still on screen (kvi-r1 #1, generalised to multi-select). The anchor
  // base is pruned alongside, or a later Cmd/Ctrl+Shift range would resurrect a
  // row the filter has hidden.
  const pruneToVisible = useCallback((visibleIds: number[]) => {
    setState((prev) => {
      // `anchorBaseIds` is always a subset of the selection, so the second half
      // of this test cannot fire on its own today; it is here so the bail-out
      // stays right if a writer ever widens the base beyond what is selected.
      if (prev.selectedIds.size === 0 && prev.anchorBaseIds.size === 0) return prev;
      const visible = new Set(visibleIds);
      const kept = [...prev.selectedIds].filter((id) => visible.has(id));
      const keptBase = [...prev.anchorBaseIds].filter((id) => visible.has(id));
      if (kept.length === prev.selectedIds.size && keptBase.length === prev.anchorBaseIds.size) {
        return prev;
      }
      return {
        ...prev,
        selectedIds: new Set<number>(kept),
        anchorBaseIds: new Set<number>(keptBase),
      };
    });
  }, []);

  const hasSelection = useMemo(() => state.selectedIds.size > 0, [state.selectedIds]);

  const selectedCount = useMemo(() => state.selectedIds.size, [state.selectedIds]);

  // True only when the view is non-empty AND every visible row is selected —
  // the bulk bar hides its "Select all N" here.
  const isAllVisibleSelected = useCallback(
    (visibleIds: number[]) =>
      visibleIds.length > 0 && visibleIds.every((id) => state.selectedIds.has(id)),
    [state.selectedIds],
  );

  return {
    selectedIds: state.selectedIds,
    hasSelection,
    selectedCount,
    selectOnly,
    toggleRow,
    selectRange,
    selectAllVisible,
    isAllVisibleSelected,
    clearSelection,
    pruneToVisible,
  };
}
